import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Switch, Text, View } from 'react-native';

// eslint-disable-next-line import/no-cycle
import { NowOnTvList, Icon, Hr, ActivityIndicator } from 'LocalGluons';
import Theme from 'Theme';
import { useStore } from 'Store';
import { t } from 'Locale';
import { Device, lockToLandscape, lockToPortrait } from 'DeviceUtils';
import { getParentalRating, itemHasEnded } from 'Utils';

import { isContentBlocked, isContentLocked } from 'Utils/parentalControlUtils';
import { ConfirmationModalContext } from 'LocalGluons/components/ConfirmationModal';
import { useLiveChannelsService } from 'LocalGluons/components/ChannelsServiceContext';

import { logEvent } from 'AppManager';
import { useEpgListMode } from 'LocalGluons/components/EpgListModeContext/EpgListModeContext';
// eslint-disable-next-line import/no-unresolved
import { PlayerData, StyleMap } from 'Types';
import { EPG_LIST_TYPES } from '../../../../../options';

import styles from './MiniEpg.jss';

interface MiniEPGProps {
    style: StyleMap;
    currentItem: PlayerData['item'];
    onItemChange: (updatedItem: PlayerData['item']) => void;
    onClose: () => void;
    hidden: boolean;
    item: PlayerData['item'];

}

const MiniEpg = (props: MiniEPGProps): React.ReactElement | null => {
    const {
        currentItem: item,
        item: newItem,
        onClose,
        style,
        hidden,
        onItemChange
    } = props;

    const { setPlayerData, parentalLockConfig, appManager } = useStore();
    const { setProtectedContent, setModalContent } = useContext(ConfirmationModalContext);
    const { refreshChannels, setChannelData } = useLiveChannelsService();
    const [loading, setLoading] = useState(false);
    const { miniEpgSelectedListMode, setMiniEpgSelectedListMode } = useEpgListMode();

    /**
     * @param {ProgramModel} live
     * @param {ProgramModel} next
     */
    const onSelect = useMemo(() => (live, next) => {
        const liveItemEnded = itemHasEnded(live);
        // If some live has ended we should update channels
        const selectedItem = liveItemEnded ? next : live;
        // Do not select current one
        if (item.getId() === selectedItem.getId()) return;

        if (liveItemEnded) refreshChannels();
        setChannelData(selectedItem.getChannelId(), selectedItem.getStartTime());

        const adultLock = isContentLocked(selectedItem.isAdult(), parentalLockConfig);
        const ageBlock = isContentBlocked(getParentalRating(selectedItem.data), parentalLockConfig);

        const successCallback = async () => {
            let updatedItem = selectedItem;
            setLoading(true);
            try {
                // Update item to get rec data
                updatedItem = await appManager.getApi().fetchProgramDetail({
                    scheduleId: selectedItem.getId()
                });
                logEvent({
                    type: "user-flow",
                    subtype: "player",
                    content1: "mini-epg",
                    content2: "channel-select",
                    context: "/player"
                });
            } catch (_) {
                console.log(`Could not update detail on ${updatedItem.getId()}`);
            }
            setLoading(false);
            onItemChange(updatedItem);
            if (Device.isPhone) lockToLandscape();
            onClose();
        };
        const closeCallback = () => {
            setModalContent(null);
            if (Device.isPhone) lockToLandscape();
        };
        const forgotPinCallback = () => {
            setPlayerData(null);
        };

        if (ageBlock || adultLock) {
            setProtectedContent(
                item,
                successCallback,
                true,
                closeCallback,
                forgotPinCallback,
                true
            );
            lockToLandscape();
        } else {
            logEvent({
                type: 'navigation',
                subtype: 'channelChange',
                content1: item.getId(),
                content2: selectedItem?.getId()
            });
            successCallback();
        }
    }, [item]);

    useEffect(() => {
        logEvent({
            type: 'navigation',
            subtype: 'miniEpg'
        });
    }, []);

    const showFavoritesOnly = miniEpgSelectedListMode === EPG_LIST_TYPES.FAVORITES;

    const onFavoritesOnlyCheckboxChanged = () => {
        const currentStateOfSwitch = !showFavoritesOnly;
        setMiniEpgSelectedListMode(
            currentStateOfSwitch ? EPG_LIST_TYPES.FAVORITES : EPG_LIST_TYPES.LIVE
        );
        logEvent({
            type: "user-flow",
            subtype: "player",
            content1: "mini-epg",
            content2: "show-favourites",
            context: "/player"
        });
    };

    const showGenreData = !Device.isWeb;

    if (hidden) return null;

    return (
        <View style={[styles.container, style]}>
            <View style={styles.headingContainer}>
                <Text style={styles.heading}>{t('COMMON.NOW_ON_TV').toUpperCase()}</Text>
                <View style={styles.switchContainer}>
                    <Switch
                        value={showFavoritesOnly}
                        onValueChange={onFavoritesOnlyCheckboxChanged}
                        trackColor={{
                            true: Theme.switchTrueColor,
                            false: Theme.switchFalseColor
                        }}
                        thumbColor="#FFF"
                    />
                    <Text style={styles.switchText}>{t('EPG.SHOW_ONLY_FAVORITES')}</Text>
                </View>
                <Icon testID="close_detail_button" name="close" onPress={onClose} />
            </View>
            {Device.isTabletOrWeb && <Hr />}
            <NowOnTvList
                blockScrollLoading={showFavoritesOnly}
                genreDataHide={showGenreData}
                hidden={hidden}
                highlightedItemId={item.getId()}
                onSelectProgram={onSelect}
                onSelectChannel={onSelect}
                passedItemData={newItem}
            />
            {loading && <ActivityIndicator isOverlay />}
        </View>
    );
};

export default MiniEpg;
